import { useGlobalReservedAppBarHeight } from '@/components/GlobalNavigationDrawer';
import { useReservedAppBarHeight } from '@/components/NavigationDrawer/NavigationDrawer';
import { fullPageViewHeightSkeletonProps } from '@/constants/skeleton';
import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import { FeatureFlag } from '@/types/featureFlag';
import { useIsDown } from '@/utils/mediaBreakpoints';
import { Children, Component } from '@/utils/reactTypes';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { Box, Breakpoint, Skeleton } from '@mui/material';
import { CSSProperties, type FC, type ForwardRefRenderFunction, Fragment, forwardRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';

type AxlePageBaseProps = {
    children: Children;
    noPadding: boolean;
};

const AxlePageBaseReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageBaseProps> = (
    props,
    ref,
) => {
    const { children, noPadding } = props;

    return (
        <Box p={noPadding ? 0 : 3} ref={ref}>
            {children}
        </Box>
    );
};

const AxlePageBase = forwardRef(AxlePageBaseReffed);

type MapSize = {
    mapBreakpoint: Breakpoint;
    mapHorizontalPercentageDefault: number;
    mapVerticalPercentageDefault: number;
    mapFirst: boolean;
};

type AxlePageWithMapProps = {
    children: Children;
    map: Component;
    mapSize: Partial<MapSize>;
    noPadding: boolean;
};

const resizeHandleStyle = (vertical: boolean): CSSProperties =>
    vertical
        ? { height: '8px', background: '#ccc', cursor: 'row-resize' }
        : { width: '8px', background: '#ccc', cursor: 'col-resize' };

const AxlePageWithMapReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageWithMapProps> = (
    props,
    ref,
) => {
    const {
        children,
        map,
        mapSize: {
            mapBreakpoint = 'md',
            mapHorizontalPercentageDefault = 50,
            mapVerticalPercentageDefault = 25,
            mapFirst = false,
        },
        noPadding,
    } = props;

    const hasGlobalNavigationV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2);
    const globalAppBarHeight = useGlobalReservedAppBarHeight();
    const legacyAppBarHeight = useReservedAppBarHeight();
    const isVerticalLayout = useIsDown(mapBreakpoint);

    const appBarHeight = hasGlobalNavigationV2 ? globalAppBarHeight : legacyAppBarHeight;
    const mapDefaultSize = isVerticalLayout
        ? mapVerticalPercentageDefault
        : mapHorizontalPercentageDefault;

    const contentPanel = (
        <Panel defaultSize={100 - mapDefaultSize} minSize={30} style={{ overflow: 'auto' }}>
            <AxlePageBase noPadding={noPadding} ref={ref}>
                {children}
            </AxlePageBase>
        </Panel>
    );

    const mapPanel = (
        <Panel defaultSize={mapDefaultSize} minSize={10}>
            <AxleErrorBoundary>{map}</AxleErrorBoundary>
        </Panel>
    );

    return (
        <Box
            sx={{
                // Both V1 and V2 reserve the nav gutter via body padding (see the desktop
                // drawers), so the box fills the body content box (100%) instead of subtracting
                // the nav width again. Only the app bar height is subtracted (mobile).
                width: '100%',
                height: `calc(100vh - ${appBarHeight})`,
                transition: 'width 0.3s ease, height 0.3s ease',
            }}
        >
            <PanelGroup
                key={isVerticalLayout ? 'vertical' : 'horizontal'}
                orientation={isVerticalLayout ? 'vertical' : 'horizontal'}
                style={{ height: '100%' }}
            >
                {mapFirst ? mapPanel : contentPanel}
                <PanelResizeHandle style={resizeHandleStyle(isVerticalLayout)} />
                {mapFirst ? contentPanel : mapPanel}
            </PanelGroup>
        </Box>
    );
};

export const AxlePageWithMap = forwardRef(AxlePageWithMapReffed);

type PageTitleProps = {
    pageTitle?: string;
};

const PageTitle: FC<PageTitleProps> = (props) => {
    const { pageTitle } = props;

    if (typeof pageTitle !== 'string') {
        return null;
    }

    return (
        <Helmet>
            <title>{pageTitle} | Axle Health</title>
        </Helmet>
    );
};

type AxlePageProps = {
    children?: Children;
    map?: Component | undefined;
    mapSize?: Partial<MapSize>;
    noPadding?: boolean;
    noNavDrawer?: boolean;
    pageTitle?: string;
    loading?: boolean;
};

const AxlePageReffed: ForwardRefRenderFunction<HTMLDivElement, AxlePageProps> = (props, ref) => {
    const { children, loading, map, mapSize = {}, noPadding = false, pageTitle } = props;
    // Both nav versions now offset the page on the body (see the desktop drawers), so no
    // per-page wrapper element is needed — keeping the tree flat preserves native scroll
    // restoration. noNavDrawer is kept for API compatibility; the result is the same Fragment.
    const Wrapper = Fragment;

    const content = loading ? <Skeleton {...fullPageViewHeightSkeletonProps} /> : children;

    if (map) {
        return (
            <Wrapper>
                <AxlePageWithMap ref={ref} map={map} mapSize={mapSize} noPadding={noPadding}>
                    <PageTitle pageTitle={pageTitle} />
                    {content}
                </AxlePageWithMap>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <AxlePageBase ref={ref} noPadding={noPadding}>
                <PageTitle pageTitle={pageTitle} />
                {content}
            </AxlePageBase>
        </Wrapper>
    );
};

export const AxlePage = forwardRef(AxlePageReffed);
